/**
 * Grooming tier definitions — issue #24 (Becky-T1.1).
 *
 * Surfaced at grooming checkout so staff can confirm the cut tier the dog
 * actually got (rather than the booked tier). Direct fix for the Yogi-class
 * undercharging captured in the 2026-05-11 on-site walkthrough:
 *
 *   - Customer booked a "basic cut"
 *   - Jorge actually gave a "profile cut"
 *   - Becky was charged at the booked tier — lost revenue
 *
 * **IMPORTANT — these are PLACEHOLDER DEFAULTS.**
 *
 * Final tier definitions (Basic / Profile / Specialty) — display label,
 * description, base-price-cents adjustment, and duration — are pending
 * sign-off by Ted + Julian + Jorge. When the locked numbers arrive, swap
 * in the tenant-config-driven version below (see `resolveGroomingTiers`).
 *
 * The defaults here are honest "best-guess" numbers calibrated against
 * Gingr's own three-tier scheme and what HTHD was charging Yogi-class
 * customers (~$130 basic; profile is the next step up). They should let
 * Becky exercise the flow end-to-end without waiting on the owner spec.
 *
 * Per-size multipliers are NOT in this initial scope — they'll come in
 * with the locked spec. Tier price is the same regardless of dog size
 * today; once Jorge's intake form is digitized (Tier-2 backlog), size
 * surcharges go here.
 *
 * Tenant override path: when `TenantConfig.groomingTiers` lands in the
 * server schema (Track-D coordination), `resolveGroomingTiers(tenant)`
 * will pull tenant-specific definitions; otherwise it falls back to
 * `DEFAULT_GROOMING_TIERS` exported here.
 */

export type GroomingTierKey = 'basic' | 'profile' | 'specialty';

export interface GroomingTierDefinition {
  /** Stable machine key — persisted on the booking record. */
  readonly key: GroomingTierKey;
  /** Display label (e.g. "Basic"). */
  readonly label: string;
  /** One-line description shown in the tier selector. */
  readonly description: string;
  /**
   * Base price for this tier, in cents. PLACEHOLDER — locked numbers
   * pending Ted + Julian + Jorge sign-off.
   */
  readonly basePriceCents: number;
  /**
   * Expected duration in minutes. PLACEHOLDER — used for at-a-glance
   * planning, not for booking-window enforcement (capacity rules drive that).
   */
  readonly durationMinutes: number;
}

export const DEFAULT_GROOMING_TIERS: ReadonlyArray<GroomingTierDefinition> = [
  {
    key: 'basic',
    label: 'Basic',
    description:
      'Bath, brush-out, nails, ears. Light trim around eyes/paws/sanitary area only.',
    basePriceCents: 8000, // $80 — placeholder per pre-spec walkthrough
    durationMinutes: 60,
  },
  {
    key: 'profile',
    label: 'Profile',
    description:
      'Full body haircut to standard breed profile or owner-specified length. Includes Basic.',
    basePriceCents: 13000, // $130 — placeholder; matches Yogi-class case
    durationMinutes: 90,
  },
  {
    key: 'specialty',
    label: 'Specialty',
    description:
      'Hand-scissored / show-style / detailed creative cuts. Includes Profile.',
    basePriceCents: 18000, // $180 — placeholder
    durationMinutes: 120,
  },
];

/**
 * Look up a tier by key. Defensive — returns `undefined` if the key was
 * persisted from an older tier set and is no longer recognized.
 */
export function findGroomingTier(
  key: string | null | undefined,
  tiers: ReadonlyArray<GroomingTierDefinition> = DEFAULT_GROOMING_TIERS,
): GroomingTierDefinition | undefined {
  if (!key) return undefined;
  return tiers.find((t) => t.key === key);
}

/**
 * Tier-confirmation pricing. Pure function, exhaustively tested.
 *
 *   - `baseTier`  — the tier the BOOKING was created at (server-side `total_cents`
 *     reflects this plus any pricing rules already applied).
 *   - `selectedTier` — the tier Becky picks at checkout (may equal `baseTier`).
 *   - `existingTotalCents` — the booking's persisted `total_cents` at the time
 *     of checkout (post pricing-rules; this is what Becky sees on the row).
 *
 * The delta is `(selected.basePriceCents - base.basePriceCents)`. Adding the
 * delta to `existingTotalCents` preserves whatever pricing-rule adjustments
 * the server already applied (size, day-of-week, new-customer discount, etc.).
 * This is the CLIENT-SIDE preview — the server MUST recalculate on submit
 * so the operator can't tamper with the persisted total.
 */
export function computeTierAdjustedTotalCents(args: {
  baseTier: GroomingTierDefinition;
  selectedTier: GroomingTierDefinition;
  existingTotalCents: number;
}): number {
  const delta = args.selectedTier.basePriceCents - args.baseTier.basePriceCents;
  return Math.max(0, args.existingTotalCents + delta);
}

/**
 * Future hook for tenant-specific tier definitions. Today it just returns
 * the defaults; when `TenantConfig.groomingTiers` ships server-side this
 * function consumes the resolved config and falls back to defaults.
 *
 * Callers should always go through this — never import `DEFAULT_GROOMING_TIERS`
 * directly from page-level code.
 */
export function resolveGroomingTiers(
  override?: ReadonlyArray<GroomingTierDefinition> | null,
): ReadonlyArray<GroomingTierDefinition> {
  if (override && override.length > 0) return override;
  return DEFAULT_GROOMING_TIERS;
}

/**
 * Heuristic: was this booking a grooming service? Used in `Bookings.tsx`
 * to decide whether to surface the tier-confirmation prompt or to skip
 * straight to the existing check-out flow.
 *
 * Today we match against ServiceTypeSummary.name (`grooming`), which is
 * the canonical machine key seeded in `apps/server/src/seed.ts` and
 * reflected in `clients/animal-lovers/...`. When daycare/boarding gain
 * their own tier-style adjustments, generalize this.
 */
export function isGroomingServiceType(
  serviceTypeName: string | null | undefined,
): boolean {
  if (!serviceTypeName) return false;
  return serviceTypeName.toLowerCase() === 'grooming';
}
